import { useState } from "react";

import { Button, Card, PageSection, StatusMessage } from "@ceedero/ui";

import { useChangePassword } from "@/account/hooks/use-account";
import { resolveCredentialChangeErrorKey } from "@/account/utils/account-refusal";
import {
  validateCurrentPassword,
  validateNewPassword,
  validatePasswordConfirmation,
} from "@/account/utils/credential-validation";
import { FormField } from "@/components/form-field";
import { useTranslation } from "@/localization/hooks/use-translation";

import type { TranslationKey } from "@/localization/hooks/use-translation";

interface FieldErrors {
  confirmation?: TranslationKey | undefined;
  currentPassword?: TranslationKey | undefined;
  newPassword?: TranslationKey | undefined;
}

const EMPTY_FORM = { confirmation: "", currentPassword: "", newPassword: "" };

/**
 * Changing the password, which is the one change here with consequences
 * elsewhere: it revokes every other session for the account — every other
 * browser, and every desktop installation's stored credential — sparing only
 * the browser that asked.
 *
 * That is said before the change rather than reported after it. Somebody
 * changing a password because they suspect it is known wants exactly this;
 * somebody tidying their account does not expect their other machines to ask
 * them to sign in again tomorrow, and finding out afterwards reads as a fault.
 *
 * The confirmation field exists because the value is masked: a typo in
 * something the user cannot read is a password only the typo knows.
 */
export const PasswordSection = () => {
  const { t } = useTranslation();
  const changePassword = useChangePassword();
  const [form, setForm] = useState(EMPTY_FORM);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

  const refusalKey = resolveCredentialChangeErrorKey(changePassword.error);

  const submit = (event: React.SyntheticEvent) => {
    event.preventDefault();
    const errors: FieldErrors = {
      confirmation: validatePasswordConfirmation({
        confirmation: form.confirmation,
        password: form.newPassword,
      }),
      currentPassword: validateCurrentPassword(form.currentPassword),
      newPassword: validateNewPassword(form.newPassword),
    };
    setFieldErrors(errors);
    if (Object.values(errors).some((error) => error !== undefined)) {
      return;
    }
    changePassword.mutate(
      { currentPassword: form.currentPassword, newPassword: form.newPassword },
      // Cleared on success rather than left filled: the work is done, and three
      // populated password fields invite a second submission of the same change
      // that would now be refused, the old password no longer being current.
      { onSuccess: () => setForm(EMPTY_FORM) },
    );
  };

  const update =
    (field: keyof typeof EMPTY_FORM) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;
      setForm((current) => ({ ...current, [field]: value }));
      setFieldErrors((errors) => ({ ...errors, [field]: undefined }));
      changePassword.reset();
    };

  return (
    <PageSection
      title={t("account.password.title")}
      description={t("account.password.description")}
    >
      <Card className="p-5">
        <form onSubmit={submit} noValidate className="flex flex-col gap-4">
          <FormField
            label={t("account.password.current")}
            type="password"
            value={form.currentPassword}
            onChange={update("currentPassword")}
            autoComplete="current-password"
            errorMessage={
              fieldErrors.currentPassword === undefined ? undefined : t(fieldErrors.currentPassword)
            }
          />

          <FormField
            label={t("account.password.new")}
            type="password"
            value={form.newPassword}
            onChange={update("newPassword")}
            hint={t("account.password.hint")}
            autoComplete="new-password"
            errorMessage={
              fieldErrors.newPassword === undefined ? undefined : t(fieldErrors.newPassword)
            }
          />

          <FormField
            label={t("account.password.confirm")}
            type="password"
            value={form.confirmation}
            onChange={update("confirmation")}
            autoComplete="new-password"
            errorMessage={
              fieldErrors.confirmation === undefined ? undefined : t(fieldErrors.confirmation)
            }
          />

          <StatusMessage tone="warning">{t("account.password.revokesOtherSessions")}</StatusMessage>

          {refusalKey !== undefined && <StatusMessage tone="error">{t(refusalKey)}</StatusMessage>}
          {changePassword.isSuccess && (
            <StatusMessage tone="success">{t("account.password.saved")}</StatusMessage>
          )}

          <div className="flex justify-end">
            <Button type="submit" isLoading={changePassword.isPending}>
              {t("account.password.submit")}
            </Button>
          </div>
        </form>
      </Card>
    </PageSection>
  );
};
